import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mic, Send, X, Square, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAgent } from "@/context/AgentContext";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";
import { speak, cancelSpeech } from "@/lib/tts";
import { api } from "@/lib/api";
import { getLocalDateString } from "@/lib/date";

/**
 * AgentDock — glass chat surface that docks under the floating orb.
 *
 * Positioning:
 *  - Anchor: centered under the orb by default.
 *  - Flip-up fallback: if the dock would overflow the viewport bottom, renders
 *    above the orb instead. Same horizontal logic either way.
 *  - Clamped horizontally so it never overflows the safe viewport edges.
 *
 * Surface:
 *  - In-memory chat history (max 6 turns) — NOT persisted across reloads.
 *  - Textarea with auto-grow + mic (Web Speech API) + send on Enter/click.
 *  - While processing a request, shows a 3-dot "typing" bubble.
 *
 * Side effects:
 *  - On each `/api/ia/interpret` response:
 *      * speaks `response` via `speak()`
 *      * if `logros` array is present, pushes it via `useAgent().pushLogrosSync`
 *        so LogrosCard refreshes without refetching
 *      * if `is_perfect_day_today: true`, fires the global celebration via
 *        `triggerPerfectDay(localDate)`
 */
const MAX_TURNS = 6;
const DOCK_WIDTH_MOBILE = 280;
const DOCK_WIDTH_DESKTOP = 320;
const DOCK_GAP = 12;
const SAFE_MARGIN = 12;

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function computeDockPosition(anchor) {
  if (typeof window === "undefined" || !anchor) {
    return { left: SAFE_MARGIN, top: SAFE_MARGIN, flipped: false, width: DOCK_WIDTH_MOBILE };
  }
  const isDesktop = window.matchMedia("(min-width: 768px)").matches;
  const width = isDesktop ? DOCK_WIDTH_DESKTOP : DOCK_WIDTH_MOBILE;
  const estimatedHeight = 320; // upper bound; actual dock maxes at 360
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Horizontal: center under orb, clamp to viewport.
  let left = anchor.centerX - width / 2;
  left = Math.max(SAFE_MARGIN, Math.min(vw - width - SAFE_MARGIN, left));

  // Vertical: below orb by default.
  let top = anchor.bottom + DOCK_GAP;
  let flipped = false;
  if (top + estimatedHeight > vh - SAFE_MARGIN) {
    // Flip up: place above the orb.
    top = anchor.top - estimatedHeight - DOCK_GAP;
    flipped = true;
    if (top < SAFE_MARGIN) {
      top = SAFE_MARGIN;
    }
  }

  return { left, top, flipped, width };
}

function TypingDots() {
  return (
    <div className="flex items-center gap-1 px-2 py-1" data-testid="agent-dock-typing">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-[#00E5FF]/70"
          style={{
            animation: "nx-typing-dot 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.18}s`,
          }}
        />
      ))}
    </div>
  );
}

function Bubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} w-full`}
      data-testid={`agent-dock-bubble-${role}`}
    >
      <div
        className={`
          max-w-[85%] rounded-2xl px-3 py-2 text-[12.5px] leading-snug
          ${isUser
            ? "bg-white/[0.06] text-[#E5E7EB] rounded-br-md"
            : "bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.18)] text-[#E5E7EB] rounded-bl-md"}
        `}
      >
        {text}
      </div>
    </div>
  );
}

export default function AgentDock() {
  const { t } = useTranslation();
  const {
    dockOpen,
    dockAnchor,
    closeDock,
    triggerPerfectDay,
    pushLogrosSync,
  } = useAgent();

  const [value, setValue] = useState("");
  const [turns, setTurns] = useState([]);
  const [processing, setProcessing] = useState(false);
  const usedVoiceRef = useRef(false);  // Phase 8: tracks if input came from mic
  const textareaRef = useRef(null);
  const historyRef = useRef(null);
  const dockRef = useRef(null);

  const { supported, listening, transcript, interim, start, stop, reset } =
    useSpeechRecognition({ lang: "es-ES" });

  const dockPos = useMemo(() => computeDockPosition(dockAnchor), [dockAnchor]);
  const date = useMemo(() => getLocalDateString(), []);

  // Sync interim transcript into the textarea while listening; keep the final
  // transcript when the user stops.
  useEffect(() => {
    if (!listening) return;
    const live = (transcript || "") + (interim ? ` ${interim}` : "");
    setValue(live.trim());
  }, [transcript, interim, listening]);

  // Auto-scroll history to bottom on new turn.
  useEffect(() => {
    if (!historyRef.current) return;
    historyRef.current.scrollTop = historyRef.current.scrollHeight;
  }, [turns, processing]);

  // Focus textarea on open.
  useEffect(() => {
    if (dockOpen && textareaRef.current) {
      const t = setTimeout(() => textareaRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
  }, [dockOpen]);

  // Escape closes.
  useEffect(() => {
    if (!dockOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeDock();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dockOpen, closeDock]);

  // Click-outside closes. We skip clicks on the orb itself (so tapping the orb
  // toggles the dock without this handler stealing it).
  useEffect(() => {
    if (!dockOpen) return;
    const onDown = (e) => {
      if (!dockRef.current) return;
      if (dockRef.current.contains(e.target)) return;
      const orb = document.querySelector('[data-testid="floating-agent"]');
      if (orb && orb.contains(e.target)) return;
      closeDock();
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [dockOpen, closeDock]);

  // Stop any lingering TTS / SR when the dock closes.
  useEffect(() => {
    if (!dockOpen) {
      cancelSpeech();
      if (listening) stop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dockOpen]);

  const handleMic = useCallback(() => {
    if (!supported) return;
    if (listening) {
      stop();
    } else {
      reset();
      cancelSpeech();
      usedVoiceRef.current = true;  // Phase 8: mark this turn as voice-sourced
      start();
    }
  }, [supported, listening, start, stop, reset]);

  const handleSend = useCallback(async () => {
    const text = value.trim();
    if (!text || processing) return;
    if (listening) stop();

    setTurns((prev) => [...prev.slice(-MAX_TURNS + 1), { role: "user", text }]);
    setValue("");
    setProcessing(true);
    try {
      const res = await api.post("/ia/interpret", {
        transcript: text,
        date,
        tz: getTimezone(),
        // Phase 8: source drives the voice gate. If the user ever toggled the
        // mic for this turn (even if they ended up editing the transcript),
        // we count it as voice — that's the conservative, paywall-safe call.
        source: usedVoiceRef.current ? "voice" : "text",
      });
      const data = res.data || {};
      const response =
        typeof data.response === "string" && data.response.trim()
          ? data.response.trim()
          : "Entendido, Sr. Fernando.";
      setTurns((prev) => [...prev.slice(-MAX_TURNS + 1), { role: "assistant", text: response }]);
      speak(response);

      if (Array.isArray(data.logros)) {
        // Phase 10: array is variable-length (0..50), not fixed at 9.
        pushLogrosSync(data.logros);
      }
      if (data.is_perfect_day_today) {
        const total = Array.isArray(data.logros) ? data.logros.length : undefined;
        triggerPerfectDay(date, total);
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg =
        status === 401
          ? t("agent.errors.session_expired")
          : t("agent.errors.generic");
      setTurns((prev) => [...prev.slice(-MAX_TURNS + 1), { role: "assistant", text: msg }]);
    } finally {
      setProcessing(false);
      usedVoiceRef.current = false;  // Reset for the next turn
    }
  }, [value, processing, date, listening, stop, pushLogrosSync, triggerPerfectDay, t]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  if (!dockOpen) return null;

  return (
    <div
      ref={dockRef}
      data-testid="agent-dock"
      data-flipped={dockPos.flipped ? "true" : "false"}
      className="nx-agent-dock"
      style={{
        position: "fixed",
        left: `${dockPos.left}px`,
        top: `${dockPos.top}px`,
        width: `${dockPos.width}px`,
        maxHeight: "360px",
        zIndex: 9000,
      }}
      role="dialog"
      aria-label={t("agent.dock.title")}
    >
      <div
        className="
          flex flex-col overflow-hidden
          rounded-[20px]
          bg-[rgba(10,14,26,0.88)] backdrop-blur-xl
          border border-[rgba(0,229,255,0.25)]
          shadow-[0_20px_60px_-10px_rgba(0,0,0,0.7),0_0_40px_-8px_rgba(0,229,255,0.35)]
          animate-nx-dock-in
        "
        style={{ maxHeight: "360px" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3.5 pt-3 pb-2 shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#00E5FF]/80">
              {t("agent.dock.assistant_label")}
            </p>
            <p className="text-[12.5px] font-semibold text-[#E5E7EB] truncate">
              {t("agent.dock.owner_label")}
            </p>
          </div>
          <button
            type="button"
            onClick={closeDock}
            data-testid="agent-dock-close"
            aria-label={t("common.close")}
            className="h-7 w-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.06] hover:text-[#E5E7EB]"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </div>

        {/* History */}
        <div
          ref={historyRef}
          className="nx-scroll flex-1 overflow-y-auto px-3.5 py-2 flex flex-col gap-2 min-h-[80px]"
          data-testid="agent-dock-history"
        >
          {turns.length === 0 && !processing ? (
            <p className="text-[12px] text-[#9CA3AF] leading-relaxed text-center px-3 py-4">
              {t("agent.dock.hint_first")}
              <br />
              <span className="text-[11px] text-[#6B7280]">
                {t("agent.dock.hint_example")}
              </span>
            </p>
          ) : null}
          {turns.slice(-MAX_TURNS).map((turn, i) => (
            <Bubble key={`${i}-${turn.role}`} role={turn.role} text={turn.text} />
          ))}
          {processing ? (
            <div className="flex justify-start w-full">
              <div className="rounded-2xl bg-[rgba(0,229,255,0.08)] border border-[rgba(0,229,255,0.18)] rounded-bl-md">
                <TypingDots />
              </div>
            </div>
          ) : null}
        </div>

        {/* Input row */}
        <div className="border-t border-white/[0.06] px-2.5 py-2 shrink-0">
          <div className="flex items-end gap-1.5">
            <button
              type="button"
              onClick={handleMic}
              disabled={!supported || processing}
              data-testid="agent-dock-mic"
              data-listening={listening ? "true" : "false"}
              aria-label={listening ? t("ia.mic.stop") : t("ia.mic.start")}
              className={`
                h-9 w-9 rounded-full shrink-0 flex items-center justify-center
                border transition-all
                ${listening
                  ? "bg-[rgba(0,229,255,0.15)] border-[rgba(0,229,255,0.6)] text-[#00E5FF] shadow-[0_0_18px_rgba(0,229,255,0.5)] animate-pulse-cyan"
                  : "bg-white/[0.03] border-white/[0.08] text-[#9CA3AF] hover:text-[#00E5FF] hover:border-[rgba(0,229,255,0.4)]"}
                disabled:opacity-40 disabled:cursor-not-allowed
              `}
            >
              {listening ? (
                <Square className="h-3.5 w-3.5" strokeWidth={2.5} fill="currentColor" />
              ) : (
                <Mic className="h-4 w-4" strokeWidth={1.75} />
              )}
            </button>

            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t("agent.dock.placeholder")}
              rows={1}
              data-testid="agent-dock-input"
              className="
                flex-1 resize-none
                bg-white/[0.03] border border-white/[0.06]
                rounded-xl px-3 py-2
                text-[13px] text-[#E5E7EB] placeholder:text-[#6B7280]
                focus:outline-none focus:border-[rgba(0,229,255,0.4)] focus:bg-white/[0.05]
                max-h-[84px]
              "
              style={{ lineHeight: "1.4" }}
            />

            <button
              type="button"
              onClick={handleSend}
              disabled={!value.trim() || processing}
              data-testid="agent-dock-send"
              aria-label={t("common.send")}
              className="
                h-9 w-9 rounded-full shrink-0 flex items-center justify-center
                bg-[rgba(0,229,255,0.12)] border border-[rgba(0,229,255,0.45)]
                text-[#00E5FF] hover:bg-[rgba(0,229,255,0.2)] transition-colors
                shadow-[0_0_14px_rgba(0,229,255,0.25)]
                disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none
              "
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
              ) : (
                <Send className="h-3.5 w-3.5" strokeWidth={2} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
